import { useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import { useMediaQuery } from 'react-responsive';
import { matchPath, Outlet, ScrollRestoration, useLocation, useMatch } from 'react-router-dom';

import { enablePlusMembershipFeatureFlag } from 'api/jalapeno';
import useAccountQuery, { ACCOUNT_QUERY_KEY } from 'api/queries/useAccountQuery';
import { PLUS_MEMBERSHIP_OPTIONS_QUERY_KEY } from 'api/queries/usePlusMembershipOptionsQuery';
import useReferralCreditsQuery from 'api/queries/useReferralCreditsQuery';
import useSubscriptionQuery from 'api/queries/useSubscriptionQuery';
import { SignupQuizStepsWithFooter } from 'constants/signup';
import { useSplitKeyReload } from 'contexts/SplitContext';
import Cart from 'features/shop/cart/Cart';
import CartSeed from 'features/shop/cart/CartSeed';
import CartSheet from 'features/shop/cart/CartSheet';
import DevBar from 'features/site/DevBar';
import Footer from 'features/site/Footer';
import Header from 'features/site/Header';
import ReferralNanoBanner from 'features/site/ReferralNanoBanner';
import SignupNanoBanner from 'features/site/SignupNanoBanner';
import useAnalyticsIdentify from 'hooks/useAnalyticsIdentify';
import { FEATURE_FLAG_NAMES, useSplitOnOffFeatureFlag } from 'hooks/useFeatureFlag';
import useHandleReferralNanoBannerScroll from 'hooks/useHandleReferralNanoBannerScroll';
import useSignupNanoBanner from 'hooks/useSignupNanoBanner';
import useStickyShopNavigation, { STICKY_SHOP_NAVIGATION_STYLES } from 'hooks/useStickyShopNavigation';
import useTailwindTheme from 'hooks/useTailwindTheme';
import useTrailingSlashRedirect from 'hooks/useTrailingSlashRedirect';
import useValidReferralsCampaign from 'hooks/useValidReferralsCampaign';
import { getIsProduction } from 'utils';
import { setUserId, updateProfile } from 'utils/analyticsUtils';
import { getSignupLocalStorage } from 'utils/signupUtils';

let HAS_FEATURE_FLAG_BEEN_UPDATED = false;

function App() {
    const { data: account = {}, isLoading: isAccountLoading } = useAccountQuery();
    const { data: referrals = {} } = useReferralCreditsQuery();
    const { data: subscription = {}, isLoading: isSubscriptionLoading } = useSubscriptionQuery();
    const queryClient = useQueryClient();

    const {
        splitIsReady,
        splitTimedOut,
        value: treatmentValue,
    } = useSplitOnOffFeatureFlag(FEATURE_FLAG_NAMES.PLUS_MEMBERSHIP);
    const inPlusMembershipTreatment = splitIsReady && !splitTimedOut ? treatmentValue : null;
    const hasPlusMembershipFeatureFlag = account?.featureFlags?.featurePaidMembership ?? null;
    const customerID = account?.customerID;

    useEffect(() => {
        if (account?.featureFlags) {
            updateProfile({
                split_membership_treatment: account?.featureFlags?.featurePaidMembership ? 'on' : 'off',
            });
        }
    }, [account?.featureFlags?.featurePaidMembership]);

    const updateFeatureFlagValue = async () => {
        if (HAS_FEATURE_FLAG_BEEN_UPDATED) {
            return;
        }

        HAS_FEATURE_FLAG_BEEN_UPDATED = true;

        const response = await enablePlusMembershipFeatureFlag({
            customerID,
        });

        if (response.msg === 'Success') {
            queryClient.invalidateQueries({ queryKey: [ACCOUNT_QUERY_KEY] });
            queryClient.invalidateQueries({ queryKey: [PLUS_MEMBERSHIP_OPTIONS_QUERY_KEY] });
        }
    };

    // If both these values are set and the split ff is true while the BE ff is false, update the BE ff
    if (
        inPlusMembershipTreatment !== null &&
        hasPlusMembershipFeatureFlag !== null &&
        inPlusMembershipTreatment &&
        !hasPlusMembershipFeatureFlag &&
        customerID
    ) {
        updateFeatureFlagValue();
    }

    useSplitKeyReload();

    const stickyShopNavigation = useStickyShopNavigation();

    const [showBoostedTermsModal, setShowBoostedTermsModal] = useState(false);
    const [appliedPromo, setAppliedPromo] = useState(null);
    const [showPromoTermsModal, setShowPromoTermsModal] = useState(false);

    const location = useLocation();
    const isAcquisitionPDPRoute = !!useMatch('/:slug/p');
    const isShopRoute = !!useMatch('/shop/*');
    const isLeadCapRoute = useMatch('/join');
    const isSignupRoute = !!useMatch('/join/*');
    const isSignupQuizRoute = !!useMatch('/join/quiz/*');
    const isAssortmentRoute = !!useMatch('/assortment-preview-module-for-mango');
    const isSignupQuizRouteWithoutFooter = isSignupQuizRoute && !SignupQuizStepsWithFooter.includes(location?.pathname);
    const isLoginRoute = !!useMatch('/login*');
    const urlParams = new URLSearchParams(window.location.search);
    const tailwindTheme = useTailwindTheme();
    const mobile = useMediaQuery({ maxWidth: tailwindTheme?.screens?.md });
    const { discountCodeData, renderSignupNanoBanner } = useSignupNanoBanner([appliedPromo]);
    const nativeApp = window.isNativeApp || urlParams.get('app') === 'true';
    const isAnalyticsReady = !isAccountLoading && !isSubscriptionLoading;
    useAnalyticsIdentify({ account, subscription, isAnalyticsReady });
    const isCartSeedRoute =
        isShopRoute ||
        ['/account', '/account/upcoming-deliveries'].find((cartSeedPath) => matchPath(cartSeedPath, location.pathname));
    const isStickyShopNavigationRoute = isShopRoute && !location.pathname.startsWith('/shop/product/');

    const referralNanoBannerVisible = useHandleReferralNanoBannerScroll();
    const validReferralsCampaign = useValidReferralsCampaign();

    useTrailingSlashRedirect();

    window.addEventListener('promoApplied', () => {
        setAppliedPromo(getSignupLocalStorage()?.discountCode);
    });

    useEffect(() => {
        if (account?.customerID) {
            setUserId(account.customerID);
        }
    }, [account?.customerID]);

    useEffect(() => {
        if (account?.brand) {
            updateProfile({ brand: account.brand });
        }
    }, [account?.brand]);

    return (
        <>
            {account?.marketplaceOpen && isCartSeedRoute && <CartSeed />}
            {!isLoginRoute && !isAssortmentRoute && (
                <div
                    className={clsx('z-[1200] w-full', {
                        [`sticky ${STICKY_SHOP_NAVIGATION_STYLES.negativeTop} ${STICKY_SHOP_NAVIGATION_STYLES.duration}`]:
                            stickyShopNavigation.hideSiteNav && mobile && isStickyShopNavigationRoute,
                        [`sticky top-0 ${STICKY_SHOP_NAVIGATION_STYLES.duration}`]:
                            !stickyShopNavigation.hideSiteNav && mobile && isStickyShopNavigationRoute,
                        'fixed top-0':
                            (!mobile || !isStickyShopNavigationRoute) &&
                            !nativeApp &&
                            !isSignupRoute &&
                            !isAcquisitionPDPRoute,
                        'sticky top-0': isAcquisitionPDPRoute,
                    })}
                >
                    {validReferralsCampaign && (
                        <ReferralNanoBanner
                            mobile={mobile}
                            referralNanoBannerVisible={referralNanoBannerVisible}
                            referralRewards={referrals?.referralRewards}
                            setShowBoostedTermsModal={setShowBoostedTermsModal}
                            showBoostedTermsModal={showBoostedTermsModal}
                        />
                    )}
                    {renderSignupNanoBanner && (
                        <SignupNanoBanner
                            mobile={mobile}
                            discountCodeData={discountCodeData}
                            setShowPromoTermsModal={setShowPromoTermsModal}
                            showPromoTermsModal={showPromoTermsModal}
                        />
                    )}
                    {!nativeApp && (
                        <Header
                            subscription={subscription}
                            account={account}
                            isShopRoute={isShopRoute}
                            isAcquisitionPDPRoute={isAcquisitionPDPRoute}
                        />
                    )}
                </div>
            )}
            <Outlet />
            {!nativeApp &&
                !isShopRoute &&
                !isSignupQuizRouteWithoutFooter &&
                !isLeadCapRoute &&
                !isLoginRoute &&
                !isAssortmentRoute && <Footer />}
            {!nativeApp && !getIsProduction() && <DevBar />}
            {!nativeApp && account?.marketplaceOpen && mobile && isShopRoute && <CartSheet mobile orderMinOnly />}
            {account?.marketplaceOpen && isShopRoute && <Cart />}
            <ScrollRestoration
                getKey={(scrollLocation) => {
                    // https://reactrouter.com/en/main/components/scroll-restoration#getkey
                    // this is used to maintain scroll restoration between pages that are not child routes of the same path
                    const paths = ['/join', '/grocery-and-produce-delivery-sneak-peek', '/:slug/p'];
                    return paths.includes(scrollLocation.pathname) ? scrollLocation.pathname : scrollLocation.key;
                }}
            />
        </>
    );
}

export default App;
